// Copyright (c) 2026, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Offer, OfferLetterState, OfferSendRequest, OfferTemplates } from "./model";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";
import { enqueueSnackbarMessage } from "@slices/commonSlice/common";
import { SnackMessage } from "@config/constant";
import { RequestState } from "@utils/types";

export const fetchOfferLetterData = createAsyncThunk(
  "offerLetter/fetchOfferLetterData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await APIService.getInstance().get(
        AppConfig.serviceUrls.offerLetters
      );
      return response.data as { offers: Offer[]; template: OfferTemplates };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Sends all selected offers.
export const sendBulkOfferLetters = createAsyncThunk(
  "offerLetter/sendBulkOfferLetters",
  async (payloads: OfferSendRequest[], { dispatch, rejectWithValue }) => {
    try {
      const response = await APIService.getInstance().post(
        AppConfig.serviceUrls.offerLetters,
        payloads
      );
      const results = response.data as {
        email: string;
        success: boolean;
        message: string;
      }[];

      const sentEmails = new Set(
        results.filter((r) => r.success).map((r) => r.email)
      );
      const sentCount = sentEmails.size;
      const failedCount = payloads.length - sentCount;

      if (failedCount > 0) {
        dispatch(
          enqueueSnackbarMessage({
            message: `Offer letter(s) failed to send`,
            type: "error",
          })
        );
      }
      if (sentCount > 0) {
        dispatch(
          enqueueSnackbarMessage({
            message: `Offer letter(s) sent successfully`,
            type: "success",
          })
        );
      }

      return payloads
        .filter((p) => sentEmails.has(p.offer.email))
        .map((p) => p.offer.rowIndex);
    } catch (error) {
      dispatch(
        enqueueSnackbarMessage({
          message: SnackMessage.error.sendOfferLetter,
          type: "error",
        })
      );
      return rejectWithValue(error);
    }
  }
);

const initialState: OfferLetterState = {
  offers: [],
  template: null,
  fetchState: RequestState.IDLE,
  sendState: RequestState.IDLE,
};

const removeAndReindex = (offers: Offer[], sentIndices: number[]): Offer[] => {
  const sorted = [...sentIndices].sort((a, b) => b - a);
  let result = [...offers];
  for (const idx of sorted) {
    result = result
      .filter((o) => o.rowIndex !== idx)
      .map((o) => ({
        ...o,
        rowIndex: o.rowIndex > idx ? o.rowIndex - 1 : o.rowIndex,
      }));
  }
  return result;
};

const offerLetterSlice = createSlice({
  name: "offerLetter",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOfferLetterData.pending, (state) => {
        state.fetchState = RequestState.LOADING;
      })
      .addCase(fetchOfferLetterData.fulfilled, (state, action) => {
        state.fetchState = RequestState.SUCCEEDED;
        state.offers = action.payload.offers;
        state.template = action.payload.template;
      })
      .addCase(fetchOfferLetterData.rejected, (state) => {
        state.fetchState = RequestState.FAILED;
      })
      .addCase(sendBulkOfferLetters.pending, (state) => {
        state.sendState = RequestState.LOADING;
      })
      .addCase(sendBulkOfferLetters.fulfilled, (state, action) => {
        state.sendState = RequestState.SUCCEEDED;
        state.offers = removeAndReindex(state.offers, action.payload);
      })
      .addCase(sendBulkOfferLetters.rejected, (state) => {
        state.sendState = RequestState.FAILED;
      });
  },
});

export default offerLetterSlice.reducer;
